import React, { useId } from 'react';
import Flex from '@wix/da-ds/pkg/Flex';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import AddIcon16 from '@wix/da-ds/pkg/Icons/16x16/Add';
import UploadedFile from '../FileUploadField/UploadedFile';
import { useDocumentUpload } from '../utils/hooks/useDocumentUpload';
import { mimeTypeToPcpType } from '../utils/mime';
import { LegalDocumentType } from '../redux/uploads/types';

interface Props {
  label: string;
  viewButtonLabel: string;
  deviationId: number;
  documentType: LegalDocumentType;
  documentUrl?: string;
  uploadButtonLabel?: string;
  onUploadComplete: (documentType: LegalDocumentType, documentUrl: string) => void;
}

const DocumentUploadField: React.FC<Props> = ({
  deviationId,
  documentType,
  documentUrl,
  label,
  viewButtonLabel,
  uploadButtonLabel,
  onUploadComplete,
}) => {
  const id = useId();
  const { input, getRootProps, activeUpload, isLoading, errorMessage, onCancel } =
    useDocumentUpload({
      deviationId,
      documentType,
      onUploadComplete: url => onUploadComplete(documentType, url),
    });

  return (
    <FormElement
      inputId={id}
      label={
        <Flex justifyContent="space-between" fullWidth>
          <InputLabelText>{label}</InputLabelText>
          {documentUrl && (
            <TextButton href={documentUrl} target="_blank" variant="main" size="small">
              {viewButtonLabel}
            </TextButton>
          )}
        </Flex>
      }
      noBottomSpacing
    >
      <div {...(isLoading ? undefined : getRootProps())}>
        {!activeUpload && (
          <GhostButton
            size="medium"
            variant="main"
            tabIndex={0}
            icon={documentUrl ? undefined : AddIcon16}
            fillContainerWidth
          >
            {uploadButtonLabel}
          </GhostButton>
        )}
        {activeUpload && (
          <UploadedFile
            name={activeUpload.file.name}
            size={activeUpload.file.size}
            type={mimeTypeToPcpType(activeUpload.file.type)}
            uploadProgress={activeUpload.progress}
            status={activeUpload.documentUrl ? 'complete' : errorMessage ? 'error' : 'loading'}
            errorMessage={errorMessage}
            onRemove={isLoading ? onCancel : undefined}
          />
        )}
        {input}
      </div>
    </FormElement>
  );
};
DocumentUploadField.displayName = 'DocumentUploadField';

export default DocumentUploadField;
